import { ProjectGeoConfig, SupportedGeo } from './config.ts'

export type RegionSettings = {
  regionConfig: Partial<Record<SupportedGeo, ProjectGeoConfig>>
  languageToRegion: Record<string, string>
  ianaToRegion: Record<string, { region: string; offsetHours: number }>
  utcOffsetTable: Record<number, string>
}

type Props = {
  lang?: string | null
  langSaved: string | null
  regionSaved: string | null
  navigatorLanguages: string[]
  ianaTimeZone: string | null | undefined
  utcOffsetHours: number | undefined
  ipAlpha2?: string | undefined
}

const supportedLanguage = (constants: RegionSettings) => {
  const s = new Set<string>()
  for (const cfg of Object.values(constants.regionConfig)) {
    for (const lang of cfg.languages) s.add(lang)
  }

  return s
}

const isLanguageSupported = (
  supported: Set<string>,
  code: string | undefined | null,
): code is string => !!code && supported.has(code)

const firstMatchingSupportedLanguage = (supported: Set<string>, tags: string[]) => {
  for (const tag of tags) {
    if (isLanguageSupported(supported, tag)) return tag
  }

  return null
}

const getLanguage = (
  constants: RegionSettings,
  supported: Set<string>,
  { lang, navigatorLanguages, langSaved }: Props,
) => {
  if (isLanguageSupported(supported, lang)) return lang

  const persisted = langSaved
  if (isLanguageSupported(supported, persisted)) return persisted

  const fromBrowser = firstMatchingSupportedLanguage(supported, navigatorLanguages)
  if (fromBrowser) return fromBrowser

  return Object.entries(constants.regionConfig)[0]?.[1].languages[0] ?? ''
}

const getRegion = (
  constants: RegionSettings,
  language: string,
  regions: Set<string>,
  { ianaTimeZone, utcOffsetHours, ipAlpha2, regionSaved }: Props,
): string => {
  if (regionSaved && regions.has(regionSaved)) return regionSaved

  const fromLang = constants.languageToRegion[language]
  if (fromLang && regions.has(fromLang)) return fromLang

  const ianaData = ianaTimeZone ? constants.ianaToRegion[ianaTimeZone] : undefined
  if (ianaData && ianaData.offsetHours === utcOffsetHours && regions.has(ianaData.region)) {
    return ianaData.region
  }

  if (utcOffsetHours) {
    const fromUtc = constants.utcOffsetTable[utcOffsetHours]
    if (fromUtc && regions.has(fromUtc)) return fromUtc
  }

  const alpha = ipAlpha2?.toUpperCase()
  if (alpha && regions.has(alpha)) return alpha

  return Object.keys(constants.regionConfig)[0]
}

export const getRegionSettings = (constants: RegionSettings, sources: Props) => {
  const allowedLang = supportedLanguage(constants)
  const allowedRegions = new Set(Object.keys(constants.regionConfig))

  const language = getLanguage(constants, allowedLang, sources)
  const region = getRegion(constants, language, allowedRegions, sources) as SupportedGeo
  const currency =
    constants.regionConfig[region]?.currency ??
    constants.regionConfig[Object.keys(constants.regionConfig)[0]! as SupportedGeo]!.currency

  return {
    region,
    currency,
    language,
    allowedLang,
    allowedRegions,
    config: constants.regionConfig[region],
  }
}
